import React, { useState } from "react";
import logo from "../../resource/7.png";
import useFormHandler from "../../Hook/useFormHandler";

const OrdinationRequest = () => {
  const initialValues = {
    first_name: "",
    last_name: "",
    date_of_birth: "",
    age: 0,
    gender: "",
    address: "",
    city: "",
    postal_code: "",
    mobile_number: "",
    whatsapp_number: "",
    email_address: "",
    ordination_type: "",
    ordination_month: "",
    ordination_year: 0,
    queries: "",
    ip_address: "",
  };

  const validations = {
    first_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "First Name should contain only letters, spaces, or hyphens",
    },
    last_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Last Name should contain only letters, spaces, or hyphens",
    },
    date_of_birth: {
      required: true,
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      message: "Date of Birth must be in YYYY-MM-DD format",
    },
    age: {
      required: true,
      pattern: /^\d+$/,
      message: "Age must be a valid number",
      validate: (value) => {
        const numValue = Number(value);
        return (numValue > 0 && numValue < 120) || "Age must be between 1 and 119";
      },
    },
    gender: {
      required: true,
      validate: (value) =>
        ["male", "female", "Other"].includes(value) || "Gender must be Male, Female, or Other",
    },
    address: {
      required: true,
      minLength: 5,
      message: "Address must be at least 5 characters long",
    },
    city: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "City should contain only letters, spaces, or hyphens",
    },
    postal_code: {
      required: true,
      pattern: /^\d{5,10}$/,
      message: "Postal Code should be 5 to 10 digits",
    },
    mobile_number: {
      required: true,
      pattern: /^\d{10}$/,
      message: "Mobile Number should be 10 digits",
    },
    whatsapp_number: {
      required: true,
      pattern: /^\d{10}$/,
      message: "WhatsApp Number should be 10 digits",
    },
    email_address: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email format",
    },
    ordination_type: {
      required: true,
      validate: (value) =>
        ["short_term", "permanent"].includes(value) || "Please select an ordination type",
    },
    ordination_month: {
      required: true,
      validate: (value) =>
        [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December",
        ].includes(value) || "Please select a valid month",
    },
    ordination_year: {
      required: true,
      pattern: /^\d{4}$/,
      message: "Year must be a 4-digit number",
      validate: (value) => {
        const year = Number(value);
        const currentYear = new Date().getFullYear();
        return (
          year >= currentYear &&
          year <= currentYear + 10
        ) || `Year must be between ${currentYear} and ${currentYear + 10}`;
      },
    },
    queries: {
      required: false,
      maxLength: 500,
      message: "Queries cannot exceed 500 characters",
    },
  };

  const endpoint = '/create-ordination-requests';

  // Transform formData keys for backend API
  const transformForApi = (data) => ({
    first_name: data.first_name,
    last_name: data.last_name,
    date_of_birth: data.date_of_birth,
    age: Number(data.age), // Ensure it's a number
    gender: data.gender,
    address: data.address,
    city: data.city,
    postal_code: data.postal_code,
    mobile_number: data.mobile_number,
    whatsapp_number: data.whatsapp_number,
    email_address: data.email_address,
    ordination_type: data.ordination_type,
    ordination_month: data.ordination_month,
    ordination_year: Number(data.ordination_year), // Ensure it's a number
    queries: data.queries,
    ip_address: data.ip_address,
  });

  const {
    formData,
    errors,
    loading,
    handleInputChange,
    handleSubmit,
    setFormData,
  } = useFormHandler({ 
    initialValues, 
    validations, 
    endpoint, 
    includeIP: true, 
    transformFn: transformForApi 
  });

  return (
    <div className="form-page dark-gold">
      <form onSubmit={handleSubmit}>
        <div className="form-title">
          <img src={logo} alt="Monastery Logo" width={200} />
          <div className="details">
            <h1 className="font-second">Bhaddeka Vihari Monastery Center</h1>
            <h2 className="h4">Ordination Request Form</h2>
          </div>
        </div>

        <p className="font-p mt-6">
          Welcome to Bhaddheka Vihari Monastery Ordination
        </p>
        <p className="font-p mt-4">
          Use the following form to fill in the layman's details.
        </p>

        <p className="font-p mt-5">Full Name *</p>

        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
            />
            <label htmlFor="first_name">First Name</label>
            {errors.first_name && <span className="error">{errors.first_name}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
            />
            <label htmlFor="last_name">Last Name</label>
            {errors.last_name && <span className="error">{errors.last_name}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="date"
              id="date_of_birth"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleInputChange}
            />
            <label htmlFor="date_of_birth">Date of birth*</label>
            {errors.date_of_birth && <span className="error">{errors.date_of_birth}</span>}
          </div>
          <div className="form-item">
            <input
              type="number"
              id="age"
              name="age"
              value={formData.age === 0 ? "" : formData.age}
              onChange={handleInputChange}
              min="1"
              max="119"
            />
            <label htmlFor="age">Age*</label>
            {errors.age && <span className="error">{errors.age}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Gender*</p>
        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="male"
              name="gender"
              value="male"
              checked={formData.gender === "male"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="male">Male</label>
          </div>
          <div>
            <input
              type="radio"
              id="female"
              name="gender"
              value="female"
              checked={formData.gender === "female"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="female">Female</label>
          </div>
          {errors.gender && <span className="error">{errors.gender}</span>}
        </div>

        <p className="font-p mt-5">Address*</p>
        <div className="form-box1">
          <div className="form-item">
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
            />
            <label htmlFor="address">Street Address</label>
            {errors.address && <span className="error">{errors.address}</span>}
          </div>
        </div>

        <div className="form-box1 mt-5">
          <div className="form-item">
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
            />
            <label htmlFor="city">City*</label>
            {errors.city && <span className="error">{errors.city}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="postal_code"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
            />
            <label htmlFor="postal_code">Postal/Zip Code*</label>
            {errors.postal_code && <span className="error">{errors.postal_code}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="tel"
              id="mobile_number"
              name="mobile_number"
              value={formData.mobile_number}
              onChange={handleInputChange}
            />
            <label htmlFor="mobile_number">Mobile Number*</label>
            {errors.mobile_number && <span className="error">{errors.mobile_number}</span>}
          </div>
          <div className="form-item">
            <input
              type="tel"
              id="whatsapp_number"
              name="whatsapp_number"
              value={formData.whatsapp_number}
              onChange={handleInputChange}
            />
            <label htmlFor="whatsapp_number">WhatsApp Number*</label>
            {errors.whatsapp_number && <span className="error">{errors.whatsapp_number}</span>}
          </div>
        </div>

        <div className="form-box1 mt-5">
          <div className="form-item">
            <input
              type="email"
              id="email_address"
              name="email_address"
              value={formData.email_address}
              onChange={handleInputChange}
            />
            <label htmlFor="email_address">Email Address*</label>
            {errors.email_address && <span className="error">{errors.email_address}</span>}
          </div>
        </div>

        <p className="font-p mt-5">
          Please indicate if you wish to be ordained short term or permanently*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="ordination_type_short">Short Term</label>
            <input
              type="radio"
              id="ordination_type_short"
              name="ordination_type"
              value="short_term"
              checked={formData.ordination_type === "short_term"}
              onChange={handleInputChange}
              className="checkbox"
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="ordination_type_permanent">Permanently</label>
            <input
              type="radio"
              id="ordination_type_permanent"
              name="ordination_type"
              value="permanent"
              checked={formData.ordination_type === "permanent"}
              onChange={handleInputChange}
              className="checkbox"
            />
          </div>
          {errors.ordination_type && <span className="error">{errors.ordination_type}</span>}
        </div>

        <p className="font-p mt-5">
          Please indicate the time you wish to get ordained*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="ordination_month">Month*</label>
            <select
              id="ordination_month"
              name="ordination_month"
              value={formData.ordination_month}
              onChange={handleInputChange}
              style={{ padding: "6px", borderRadius: "4px" }}
            >
              <option value="">Select Month</option>
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </select>
            {errors.ordination_month && <span className="error">{errors.ordination_month}</span>}
          </div>
          
          <div className="checkbox-box">
            <label htmlFor="ordination_year">Year*</label>
            <input
              type="number"
              id="ordination_year"
              name="ordination_year"
              value={formData.ordination_year === 0 ? "" : formData.ordination_year}
              onChange={handleInputChange}
              min={new Date().getFullYear()}
              max={new Date().getFullYear() + 10}
              style={{ width: "80px", padding: "6px", borderRadius: "4px" }}
            />
            {errors.ordination_year && <span className="error">{errors.ordination_year}</span>}
          </div>
        </div>

        <p className="font-p mt-5">
          Please write any specific questions or queries you would like to
          clarify getting ordained at the Monastery
        </p>

        <div className="mt-5">
          <div className="form-item">
            <textarea
              id="queries"
              name="queries"
              value={formData.queries}
              onChange={handleInputChange}
              rows="6"
              placeholder="Enter your questions or queries here..."
            />
            <label htmlFor="queries">Queries (Optional)</label>
            {errors.queries && <span className="error">{errors.queries}</span>}
          </div>
        </div>

        <button
          type="submit"
          id="submitbtn"
          className="mt-5"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>

        <p className="font-p mt-5 pb-4">
          We look forward to facilitating your request and are grateful for
          choosing Bhaddheka Vihari Monastery as an enriching space for practicing
          mindfulness meditation.
        </p>
      </form>
    </div>
  );
};

export default OrdinationRequest;